import { CheckCircleOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Form, message, Modal, Select, Space, Tag, Typography } from 'antd';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

const { Text, Title } = Typography;
const { Option } = Select;

const EvaluateCandidateModal = ({ visible, onCancel, candidate, onSuccess }) => {
  const [form] = Form.useForm();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [evaluationResult, setEvaluationResult] = useState(null);

  // Fetch available job descriptions
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        message.error('No authentication token found. Please log in again.');
        return;
      }

      const response = await axios.get('/api/job_descriptions', {
        params: {
          limit: 10
        },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setJobs(response.data.job_descriptions || []);
        if (response.data.job_descriptions.length === 0) {
          message.warning('No job descriptions available. Please upload a job description first.');
        }
      } else {
        message.error('Failed to fetch job descriptions');
      }
    } catch (error) {
      console.error('Error fetching job descriptions:', error);
      message.error(error.response?.data?.error || 'Failed to fetch job descriptions');
    } finally {
      setLoading(false);
    }
  };

  // Handle job selection
  const handleJobSelect = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setSelectedJob(job);
    setEvaluationResult(null); // Clear previous evaluation when job changes
  };

  // Handle evaluation
  const handleEvaluate = async () => {
    if (!selectedJob) {
      message.error('Please select a job first');
      return;
    }

    try {
      setEvaluating(true);
      const token = localStorage.getItem('token');

      if (!token) {
        message.error('No authentication token found. Please log in again.');
        return;
      }

      const response = await axios.post(`/api/candidates/${candidate.id}/evaluate`, {
        job_id: selectedJob.id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success('Evaluation completed successfully!');
        setEvaluationResult(response.data.evaluation);

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess(response.data.evaluation, selectedJob);
        }
      } else {
        message.error(response.data.error || 'Failed to evaluate candidate');
      }
    } catch (error) {
      console.error('Error evaluating candidate:', error);
      message.error(error.response?.data?.error || 'Failed to evaluate candidate');
    } finally {
      setEvaluating(false);
    }
  };

  // Fetch jobs when modal opens
  useEffect(() => {
    if (visible) {
      fetchJobs();
      // Reset form and state when modal opens
      form.resetFields();
      setSelectedJob(null);
      setEvaluationResult(null);
    }
  }, [visible]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'orange';
    return 'red';
  };

  return (
    <Modal
      title="Evaluate Candidate for Job"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: '16px' }}>
        <Text strong>Candidate: </Text>
        <Text>{candidate?.contactName || 'Unknown'}</Text>
      </div>

      <Form form={form} layout="vertical">
        <Form.Item label="Select Job" required>
          <Select
            placeholder="Select a job to evaluate against"
            value={selectedJob?.id}
            onChange={handleJobSelect}
            loading={loading}
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            notFoundContent={loading ? <LoadingOutlined /> : 'No jobs found'}
          >
            {jobs.map(job => (
              <Option key={job.id} value={job.id}>
                {job.title} - {job.company}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedJob && (
          <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#f5f5f5' }}>
            <Text strong>Selected Job: </Text>
            <Text>{selectedJob.title} at {selectedJob.company}</Text>
          </Card>
        )}

        <Form.Item>
          <Button
            type="primary"
            icon={evaluating ? <LoadingOutlined /> : <SearchOutlined />}
            onClick={handleEvaluate}
            loading={evaluating}
            disabled={!selectedJob || evaluating}
            block
          >
            {evaluating ? 'Evaluating...' : 'Evaluate Candidate'}
          </Button>
        </Form.Item>
      </Form>

      {evaluationResult && (
        <div style={{ marginTop: '24px' }}>
          <Divider />
          <Title level={4}>Evaluation Results</Title>

          <Card>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* Score */}
              <div>
                <Text strong>Overall Score: </Text>
                <Tag color={getScoreColor(evaluationResult.score)} style={{ fontSize: '16px', padding: '4px 12px' }}>
                  {evaluationResult.score.toFixed(1)}/100
                </Tag>
              </div>

              {/* Explanation */}
              {evaluationResult.explanation && (
                <div>
                  <Text strong>Explanation:</Text>
                  <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                    <Text>{evaluationResult.explanation}</Text>
                  </div>
                </div>
              )}

              {/* Key Matches */}
              {evaluationResult.key_matches && evaluationResult.key_matches.length > 0 && (
                <div>
                  <Text strong>Key Matches:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {evaluationResult.key_matches.map((match, index) => (
                      <Tag key={index} color="green" style={{ marginBottom: '4px' }}>
                        <CheckCircleOutlined /> {match}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Strengths */}
              {evaluationResult.strengths && evaluationResult.strengths.length > 0 && (
                <div>
                  <Text strong>Strengths:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {evaluationResult.strengths.map((strength, index) => (
                      <Tag key={index} color="blue" style={{ marginBottom: '4px' }}>
                        {strength}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Weaknesses */}
              {evaluationResult.weaknesses && evaluationResult.weaknesses.length > 0 && (
                <div>
                  <Text strong>Weaknesses:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {evaluationResult.weaknesses.map((weakness, index) => (
                      <Tag key={index} color="orange" style={{ marginBottom: '4px' }}>
                        {weakness}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Skills */}
              {evaluationResult.missing_skills && evaluationResult.missing_skills.length > 0 && (
                <div>
                  <Text strong>Missing Skills:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {evaluationResult.missing_skills.map((skill, index) => (
                      <Tag key={index} color="red" style={{ marginBottom: '4px' }}>
                        {skill}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {evaluationResult.recommendations && evaluationResult.recommendations.length > 0 && (
                <div>
                  <Text strong>Recommendations:</Text>
                  <div style={{ marginTop: '8px' }}>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {evaluationResult.recommendations.map((rec, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>
                          <Text>{rec}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </Space>
          </Card>
        </div>
      )}
    </Modal>
  );
};

export default EvaluateCandidateModal;
